import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Select } from "@repo/o37-ui";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Check,
  ChevronRight,
  Target,
  CreditCard
} from "lucide-react";
import { formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface SubscriptionVariance {
  id: string;
  vendorName: string;
  expectedCents: number;
  actualCents: number;
  varianceCents: number;
  variancePercent: number;
}

interface ForecastVariance {
  id: string;
  companyName: string;
  companyId: string;
  category: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  expectedCents: number;
  actualCents: number;
  varianceCents: number;
  variancePercent: number;
}

interface AnalyticsData {
  costs?: {
    expectedCents: number;
    actualCents: number;
    varianceCents: number;
    variancePercent: number;
    bySubscription: SubscriptionVariance[];
  };
  revenue?: {
    expectedCents: number;
    actualCents: number;
    varianceCents: number;
    variancePercent: number;
    byForecast: ForecastVariance[];
  };
}

export default function ExpectedVsActualDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  // Period options (last 12 months + next 3 months)
  const periodOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();

    for (let i = -12; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Parse period into start/end dates
      const [year, month] = period.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];

      const response = await fetch(
        `${API_URL}/holdings/analytics/expected-vs-actual?startDate=${startDate}&endDate=${endDate}&type=all`,
        {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (e) {
      console.error('Failed to load analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [period]);

  const getCategoryLabel = (key: string) => {
    const labels: Record<string, string> = {
      retainer: 'Retainer',
      subscription: 'Subscription',
      one_time: 'One-time',
      project: 'Project',
      consulting: 'Consulting',
      commission: 'Commission',
      licensing: 'Licensing',
      other: 'Other'
    };
    return labels[key] || key;
  };

  const VarianceIndicator = ({ variance, percent }: { variance: number; percent: number }) => {
    const isPositive = variance >= 0;
    const isSignificant = Math.abs(percent) > 10;

    if (Math.abs(percent) <= 5) {
      return (
        <div className="flex items-center gap-1.5 text-emerald-600">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">On track</span>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-1.5 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
        {isPositive ? (
          <TrendingUp className="w-4 h-4" />
        ) : (
          <TrendingDown className="w-4 h-4" />
        )}
        <span className="text-sm font-medium">
          {isPositive ? '+' : ''}{percent.toFixed(1)}%
        </span>
        {isSignificant && !isPositive && (
          <AlertTriangle className="w-4 h-4 text-amber-500" />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Expected vs Actual</h1>
        <p className="text-neutral-500">Loading analytics...</p>
      </div>
    );
  }

  const periodLabel = periodOptions.find(p => p.value === period)?.label || period;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Expected vs Actual</h1>
          <p className="text-neutral-500">Track your financial performance against expectations</p>
        </div>
        <div className="w-48">
          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Costs Summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">Costs</h3>
                  <p className="text-sm text-neutral-500">{periodLabel}</p>
                </div>
              </div>
              <VarianceIndicator
                variance={data?.costs?.varianceCents || 0}
                percent={data?.costs?.variancePercent || 0}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Expected</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {formatCents(data?.costs?.expectedCents || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Actual</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {formatCents(data?.costs?.actualCents || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Variance</p>
                <p className={`text-lg font-semibold ${
                  (data?.costs?.varianceCents || 0) > 0 ? 'text-red-600' : 'text-emerald-600'
                }`}>
                  {(data?.costs?.varianceCents || 0) > 0 ? '+' : ''}
                  {formatCents(data?.costs?.varianceCents || 0)}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    (data?.costs?.variancePercent || 0) > 10 ? 'bg-red-500' :
                    (data?.costs?.variancePercent || 0) > 0 ? 'bg-amber-400' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, ((data?.costs?.actualCents || 0) / (data?.costs?.expectedCents || 1)) * 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">Revenue</h3>
                  <p className="text-sm text-neutral-500">{periodLabel}</p>
                </div>
              </div>
              <VarianceIndicator
                variance={data?.revenue?.varianceCents || 0}
                percent={data?.revenue?.variancePercent || 0}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Expected</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {formatCents(data?.revenue?.expectedCents || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Actual</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {formatCents(data?.revenue?.actualCents || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Variance</p>
                <p className={`text-lg font-semibold ${
                  (data?.revenue?.varianceCents || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {(data?.revenue?.varianceCents || 0) >= 0 ? '+' : ''}
                  {formatCents(data?.revenue?.varianceCents || 0)}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    (data?.revenue?.variancePercent || 0) < -10 ? 'bg-red-500' :
                    (data?.revenue?.variancePercent || 0) < 0 ? 'bg-amber-400' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, ((data?.revenue?.actualCents || 0) / (data?.revenue?.expectedCents || 1)) * 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Net Position */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Net Position ({periodLabel})</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Expected Net</p>
              <p className="text-xl font-semibold text-neutral-900">
                {formatCents((data?.revenue?.expectedCents || 0) - (data?.costs?.expectedCents || 0))}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Actual Net</p>
              <p className="text-xl font-semibold text-neutral-900">
                {formatCents((data?.revenue?.actualCents || 0) - (data?.costs?.actualCents || 0))}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Net Variance</p>
              {(() => {
                const expectedNet = (data?.revenue?.expectedCents || 0) - (data?.costs?.expectedCents || 0);
                const actualNet = (data?.revenue?.actualCents || 0) - (data?.costs?.actualCents || 0);
                const netVariance = actualNet - expectedNet;
                return (
                  <p className={`text-xl font-semibold ${netVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {netVariance >= 0 ? '+' : ''}{formatCents(netVariance)}
                  </p>
                );
              })()}
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Status</p>
              {(() => {
                const expectedNet = (data?.revenue?.expectedCents || 0) - (data?.costs?.expectedCents || 0);
                const actualNet = (data?.revenue?.actualCents || 0) - (data?.costs?.actualCents || 0);
                const netVariance = actualNet - expectedNet;
                const pct = expectedNet !== 0 ? (netVariance / Math.abs(expectedNet)) * 100 : 0;

                if (Math.abs(pct) <= 10) {
                  return (
                    <div className="flex items-center gap-1.5 text-emerald-600">
                      <Check className="w-5 h-5" />
                      <span className="text-xl font-semibold">On Track</span>
                    </div>
                  );
                } else if (netVariance > 0) {
                  return (
                    <div className="flex items-center gap-1.5 text-emerald-600">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-xl font-semibold">Ahead</span>
                    </div>
                  );
                } else {
                  return (
                    <div className="flex items-center gap-1.5 text-red-600">
                      <TrendingDown className="w-5 h-5" />
                      <span className="text-xl font-semibold">Behind</span>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Variances */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-neutral-900">Cost Variances</h3>
              <Button
                variant="outline"
                className="text-xs h-8"
                onClick={() => navigate('/holdings/costs')}
                icon={<ChevronRight className="w-3.5 h-3.5" />}
                text="View All"
              />
            </div>

            {(!data?.costs?.bySubscription || data.costs.bySubscription.length === 0) ? (
              <div className="text-center py-8 text-neutral-500">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
                <p>No subscription data for this period</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.costs.bySubscription.slice(0, 5).map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                    <div>
                      <p className="font-medium text-neutral-900">{sub.vendorName}</p>
                      <p className="text-sm text-neutral-500">
                        Expected: {formatCents(sub.expectedCents)} | Actual: {formatCents(sub.actualCents)}
                      </p>
                    </div>
                    <div className={`text-right ${sub.varianceCents > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      <p className="font-semibold">
                        {sub.varianceCents > 0 ? '+' : ''}{formatCents(sub.varianceCents)}
                      </p>
                      <p className="text-xs">
                        {sub.variancePercent > 0 ? '+' : ''}{sub.variancePercent.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Variances */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-neutral-900">Revenue Variances</h3>
              <Button
                variant="outline"
                className="text-xs h-8"
                onClick={() => navigate('/holdings/revenue/forecasts')}
                icon={<ChevronRight className="w-3.5 h-3.5" />}
                text="View All"
              />
            </div>

            {(!data?.revenue?.byForecast || data.revenue.byForecast.length === 0) ? (
              <div className="text-center py-8 text-neutral-500">
                <Target className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
                <p>No forecast data for this period</p>
                <Button
                  variant="outline"
                  className="mt-3 text-xs"
                  onClick={() => navigate('/holdings/revenue/forecasts')}
                  text="Create Forecast"
                />
              </div>
            ) : (
              <div className="space-y-3">
                {data.revenue.byForecast.slice(0, 5).map((forecast) => (
                  <div key={forecast.id} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                    <div>
                      <p className="font-medium text-neutral-900">{forecast.companyName}</p>
                      <p className="text-sm text-neutral-500">
                        {getCategoryLabel(forecast.category)} | Expected: {formatCents(forecast.expectedCents)}
                      </p>
                    </div>
                    <div className={`text-right ${forecast.varianceCents >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      <p className="font-semibold">
                        {forecast.varianceCents >= 0 ? '+' : ''}{formatCents(forecast.varianceCents)}
                      </p>
                      <p className="text-xs">
                        {forecast.variancePercent >= 0 ? '+' : ''}{forecast.variancePercent.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      {((data?.costs?.bySubscription?.some(s => s.variancePercent > 20)) ||
        (data?.revenue?.byForecast?.some(f => f.variancePercent < -20))) && (
        <Card className="mt-6 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Attention Required</h3>
                <ul className="space-y-1 text-sm text-amber-800">
                  {data?.costs?.bySubscription?.filter(s => s.variancePercent > 20).map(s => (
                    <li key={s.id}>
                      <strong>{s.vendorName}</strong> is {s.variancePercent.toFixed(0)}% over budget
                    </li>
                  ))}
                  {data?.revenue?.byForecast?.filter(f => f.variancePercent < -20).map(f => (
                    <li key={f.id}>
                      <strong>{f.companyName} {getCategoryLabel(f.category)}</strong> is {Math.abs(f.variancePercent).toFixed(0)}% under forecast
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
